import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Edit, Eye, Home, Plus, Settings, Star, Trash2 } from "lucide-react";
import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import PricingConfigurator from "@/components/templates/PricingConfigurator";
import TemplateBuilder from "@/components/templates/TemplateBuilder";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { ChecklistTemplate, TemplateType } from "@/types/components";

const TemplateManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<ChecklistTemplate | null>(null);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [showPricingConfigurator, setShowPricingConfigurator] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);
  const [newTemplateData, setNewTemplateData] = useState({
    name: "",
    description: "",
    template_type: "bathroom" as TemplateType,
    is_default: false,
    service_id: null as number | null,
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<ChecklistTemplate | null>(null);

  const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

  // Fetch templates
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/templates`);
      if (!response.ok) throw new Error("Failed to fetch templates");
      const result = await response.json();
      return result.data || [];
    },
  });

  // Fetch services for template creation
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services`);
      if (!response.ok) throw new Error("Failed to fetch services");
      const result = await response.json();
      return result.data || [];
    },
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: async (
      templateData: Omit<ChecklistTemplate, "id" | "created_at" | "updated_at">
    ) => {
      const response = await fetch(`${apiUrl}/api/v1/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(templateData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create template");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setShowCreateDialog(false);
      resetNewTemplateData();
      toast({
        title: "Template Created",
        description: "Template has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create template",
        variant: "destructive",
      });
    },
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<ChecklistTemplate> & { id: number }) => {
      const response = await fetch(`${apiUrl}/api/v1/templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update template");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEditingTemplate(null);
      toast({
        title: "Template Updated",
        description: "Template has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update template",
        variant: "destructive",
      });
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const response = await fetch(`${apiUrl}/api/v1/templates/${templateId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete template");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast({
        title: "Template Deleted",
        description: "Template has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete template",
        variant: "destructive",
      });
    },
  });

  // Set default template mutation
  const setDefaultTemplateMutation = useMutation({
    mutationFn: async ({
      templateId,
      templateType,
    }: {
      templateId: number;
      templateType: TemplateType;
    }) => {
      const response = await fetch(`${apiUrl}/api/v1/templates/${templateId}/set-default`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ template_type: templateType }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to set default template");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast({
        title: "Default Template Set",
        description: "Template has been set as default successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set default template",
        variant: "destructive",
      });
    },
  });

  // Duplicate template mutation
  const duplicateTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const response = await fetch(`${apiUrl}/api/v1/templates/${templateId}/duplicate`, {
        method: "POST",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to duplicate template");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast({
        title: "Template Duplicated",
        description: "Template has been duplicated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to duplicate template",
        variant: "destructive",
      });
    },
  });

  const resetNewTemplateData = () => {
    setNewTemplateData({
      name: "",
      description: "",
      template_type: "bathroom" as TemplateType,
      is_default: false,
      service_id: null,
    });
  };

  const handleCreateTemplate = () => {
    if (!newTemplateData.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a template name.",
        variant: "destructive",
      });
      return;
    }

    createTemplateMutation.mutate({
      ...newTemplateData,
      is_active: true,
      display_order: templates.length + 1,
      template_data: {},
      pricing_data: {},
    });
  };

  const handleEditTemplate = (template: ChecklistTemplate) => {
    setActiveTemplateId(template.id);
    setShowTemplateBuilder(true);
  };

  const handleConfigurePricing = (template: ChecklistTemplate) => {
    setActiveTemplateId(template.id);
    setShowPricingConfigurator(true);
  };

  const handlePreviewTemplate = (template: ChecklistTemplate) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleSetDefault = (template: ChecklistTemplate) => {
    setDefaultTemplateMutation.mutate({
      templateId: template.id,
      templateType: template.template_type,
    });
  };

  const handleDuplicateTemplate = (templateId: number) => {
    duplicateTemplateMutation.mutate(templateId);
  };

  const handleDeleteTemplate = (templateId: number) => {
    if (confirm("Are you sure you want to delete this template? This action cannot be undone.")) {
      deleteTemplateMutation.mutate(templateId);
    }
  };

  const getTemplateTypeLabel = (type: TemplateType): string => {
    switch (type) {
      case "bathroom":
        return "Bathroom";
      case "kitchen":
        return "Kitchen";
      case "laundry":
        return "Laundry";
      case "whole_home":
        return "Whole Home";
      case "custom":
        return "Custom";
      default:
        return type;
    }
  };

  const getTemplateTypeColor = (type: TemplateType): string => {
    switch (type) {
      case "bathroom":
        return "bg-blue-100 text-blue-800";
      case "kitchen":
        return "bg-green-100 text-green-800";
      case "laundry":
        return "bg-purple-100 text-purple-800";
      case "whole_home":
        return "bg-orange-100 text-orange-800";
      case "custom":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Group templates by type
  const templatesByType = templates.reduce(
    (acc: Record<TemplateType, ChecklistTemplate[]>, template) => {
      if (!acc[template.template_type]) {
        acc[template.template_type] = [];
      }
      acc[template.template_type].push(template);
      return acc;
    },
    {} as Record<TemplateType, ChecklistTemplate[]>
  );

  if (templatesLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Loading templates...</div>
        </div>
      </DashboardLayout>
    );
  }

  // Handle modals
  if (showTemplateBuilder) {
    return (
      <DashboardLayout>
        <TemplateBuilder
          templateId={activeTemplateId}
          onClose={() => {
            setShowTemplateBuilder(false);
            setActiveTemplateId(null);
          }}
        />
      </DashboardLayout>
    );
  }

  if (showPricingConfigurator) {
    return (
      <DashboardLayout>
        <PricingConfigurator
          templateId={activeTemplateId}
          onClose={() => {
            setShowPricingConfigurator(false);
            setActiveTemplateId(null);
          }}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Template Management</h1>
            <p className="text-gray-600 mt-2">
              Create and manage quote templates for different renovation types
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => (window.location.href = "/")}>
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild={true}>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Template</DialogTitle>
                  <DialogDescription>
                    Create a new quote template for a specific renovation type
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="template-name">Template Name</Label>
                    <Input
                      id="template-name"
                      placeholder="e.g., Master Bathroom Standard"
                      value={newTemplateData.name}
                      onChange={(e) =>
                        setNewTemplateData((prev) => ({ ...prev, name: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-description">Description</Label>
                    <Textarea
                      id="template-description"
                      placeholder="Describe what this template covers..."
                      value={newTemplateData.description}
                      onChange={(e) =>
                        setNewTemplateData((prev) => ({ ...prev, description: e.target.value }))
                      }
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-type">Template Type</Label>
                    <Select
                      value={newTemplateData.template_type}
                      onValueChange={(value: TemplateType) =>
                        setNewTemplateData((prev) => ({ ...prev, template_type: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bathroom">Bathroom</SelectItem>
                        <SelectItem value="kitchen">Kitchen</SelectItem>
                        <SelectItem value="laundry">Laundry</SelectItem>
                        <SelectItem value="whole_home">Whole Home</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="service">Associated Service (Optional)</Label>
                    <Select
                      value={newTemplateData.service_id?.toString() || ""}
                      onValueChange={(value) =>
                        setNewTemplateData((prev) => ({
                          ...prev,
                          service_id: value ? Number.parseInt(value, 10) : null,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-service">No service</SelectItem>
                        {services.map((service: any) => (
                          <SelectItem key={service.id} value={service.id.toString()}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="set-default"
                      checked={newTemplateData.is_default}
                      onCheckedChange={(checked) =>
                        setNewTemplateData((prev) => ({ ...prev, is_default: checked }))
                      }
                    />
                    <Label htmlFor="set-default">Set as default template for this type</Label>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateTemplate}
                      disabled={createTemplateMutation.isPending}
                    >
                      {createTemplateMutation.isPending ? "Creating..." : "Create Template"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Template Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {(["bathroom", "kitchen", "laundry", "whole_home", "custom"] as TemplateType[]).map(
            (type) => {
              const count = templatesByType[type]?.length || 0;
              const defaultTemplate = templatesByType[type]?.find((t) => t.is_default);

              return (
                <Card key={type}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">{getTemplateTypeLabel(type)}</p>
                        <p className="text-2xl font-bold">{count}</p>
                        {defaultTemplate && (
                          <p className="text-xs text-green-600 flex items-center mt-1">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Default set
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }
          )}
        </div>

        {/* Templates by Type */}
        <Tabs defaultValue="bathroom" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="bathroom">Bathroom</TabsTrigger>
            <TabsTrigger value="kitchen">Kitchen</TabsTrigger>
            <TabsTrigger value="laundry">Laundry</TabsTrigger>
            <TabsTrigger value="whole_home">Whole Home</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>

          {(["bathroom", "kitchen", "laundry", "whole_home", "custom"] as TemplateType[]).map(
            (type) => (
              <TabsContent key={type} value={type} className="space-y-4">
                {templatesByType[type]?.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templatesByType[type].map((template) => (
                      <Card key={template.id} className="relative">
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between text-lg">
                            <div className="flex items-center gap-2">
                              {template.name}
                              {template.is_default && (
                                <Badge
                                  variant="secondary"
                                  className="bg-yellow-100 text-yellow-800"
                                >
                                  <Star className="w-3 h-3 mr-1 fill-current" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            <Badge className={getTemplateTypeColor(template.template_type)}>
                              {getTemplateTypeLabel(template.template_type)}
                            </Badge>
                          </CardTitle>
                        </CardHeader>

                        <CardContent className="space-y-4">
                          {template.description && (
                            <p className="text-sm text-gray-600">{template.description}</p>
                          )}

                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span>Active: {template.is_active ? "Yes" : "No"}</span>
                            <span>
                              Updated: {new Date(template.updated_at).toLocaleDateString()}
                            </span>
                          </div>

                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditTemplate(template)}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleConfigurePricing(template)}
                            >
                              <Settings className="w-3 h-3 mr-1" />
                              Pricing
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePreviewTemplate(template)}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Preview
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDuplicateTemplate(template.id)}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Duplicate
                            </Button>

                            {!template.is_default && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSetDefault(template)}
                                disabled={setDefaultTemplateMutation.isPending}
                              >
                                <Star className="w-3 h-3 mr-1" />
                                Set Default
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">
                      No {getTemplateTypeLabel(type).toLowerCase()} templates yet
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setNewTemplateData((prev) => ({ ...prev, template_type: type }));
                        setShowCreateDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create {getTemplateTypeLabel(type)} Template
                    </Button>
                  </div>
                )}
              </TabsContent>
            )
          )}
        </Tabs>
      </div>

      {/* Template Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Template Preview: {previewTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Preview how this template will appear in the quote builder
            </DialogDescription>
          </DialogHeader>

          {previewTemplate && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Template Type</p>
                  <p className="font-medium">
                    {getTemplateTypeLabel(previewTemplate.template_type)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <Badge variant={previewTemplate.is_active ? "default" : "secondary"}>
                    {previewTemplate.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Default Template</p>
                  <p className="font-medium">{previewTemplate.is_default ? "Yes" : "No"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="font-medium">
                    {new Date(previewTemplate.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {previewTemplate.description && (
                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-gray-600">{previewTemplate.description}</p>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">Template Structure</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm">
                    {JSON.stringify(previewTemplate.template_data || {}, null, 2)}
                  </pre>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Pricing Configuration</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm">
                    {JSON.stringify(previewTemplate.pricing_data || {}, null, 2)}
                  </pre>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    handleEditTemplate(previewTemplate);
                    setShowPreview(false);
                  }}
                >
                  Edit Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default TemplateManagement;
